import { useState, useEffect } from 'react'
import message from 'antd/lib/message'
import Form from 'antd/lib/form'

import { commonHelper } from 'helpers'
import { useGetCustomers, useGetExpenditureDetails } from 'services/clients/clients.service'

import { commonUtils, eventManager } from 'utils'
import { addEditExpenditureDetailsMutation } from 'gql/client'
import { useCustomMutation } from 'services/shared/mutation'
import { AddEditExpenditureDetailsPayload } from 'services/shared/mutation.payload'
import { getCustomersOptions } from 'utils/getCustomersOptions'

const initialState = {
  isOpen: false,
  data: { _id: '', isEdit: false },
  cb: { confirm: () => null, cancel: () => null },
  loading: false,
}

const useExpenditureModal = () => {
  const [form] = Form.useForm()
  const addEditExpenditureDetails = useCustomMutation<AddEditExpenditureDetailsPayload>({
    mutation: addEditExpenditureDetailsMutation,
    refetchQueries: ['getExpendituresDetails'],
  })
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  const [modalState, setModalState] = useState(initialState)
  const expenditureDetailsId = modalState?.data?._id
  const { expenditureDetails, loading: initialDataLoading } = useGetExpenditureDetails({
    idNumber,
    expenditureDetailsId,
    skip: !modalState.isOpen || !modalState.data.isEdit,
  })
  const [loading, setLoading] = useState(initialDataLoading)
  const [formData, setFormData] = useState({
    expenditureHolder: expenditureDetails?.expenditureHolder,
    expenditurePeriod: expenditureDetails?.expenditurePeriod,
    expenditureType: expenditureDetails?.expenditureType,
    expenditureAmount: expenditureDetails?.expenditureAmount,
    description: expenditureDetails?.description,
  })

  const { data: customersData } = useGetCustomers(idNumber)

  const customersOptions = getCustomersOptions(customersData, true)

  useEffect(() => {
    setLoading(initialDataLoading)
    const newData = commonUtils.removeTypeNameFromObject(expenditureDetails)
    delete newData._id
    setFormData({ ...newData })
    form.setFieldsValue({ ...newData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoading])

  const clearFormData = () => {
    setFormData((prevState) => commonUtils.resetFormData(prevState))
    form.setFieldsValue(commonUtils.resetFormData(formData))
  }

  const handleCustomSelectChange = (name: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleAntdFormInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  const handleCustomTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  useEffect(() => {
    eventManager.addListener('expenditure-modal', (data: any, cb: any) => {
      setModalState((state) => ({
        ...state,
        isOpen: data !== undefined,
        data,
        cb,
      }))
    })
  }, [setModalState])

  const handleClose = () => {
    setModalState((state) => ({ ...state, loading: false }))
    eventManager.closeExpenditureModal()
  }

  const handleCancelClick = async () => {
    try {
      setModalState((state) => ({ ...state, loading: true }))
      if (modalState.cb.cancel) {
        await modalState.cb.cancel()
      }
    } catch (err) {
      // console.error(err)
    }
    handleClose()
    clearFormData()
  }

  const handleSubmit = async () => {
    const { errors } = await addEditExpenditureDetails({
      idNumber,
      expenditureDetailsToEdit: expenditureDetailsId,
      values: {
        ...formData,
        expenditureAmount: Number(formData.expenditureAmount),
      },
    })
    if (errors?.length) {
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Operation finished successfully')
  }

  const handleConfirmClick = async () => {
    try {
      setModalState((state) => ({ ...state, loading: true }))
      await handleSubmit()
      clearFormData()
      if (modalState.cb.confirm) {
        await modalState.cb.confirm()
      }
    } catch (err) {
      // console.error(err)
    }
    setModalState((state) => ({ ...state, loading: false }))
    handleClose()
  }

  return {
    modalState,
    handleCancelClick,
    handleConfirmClick,
    formData,
    handleCustomSelectChange,
    handleAntdFormInputChange,
    handleCustomTextAreaChange,
    loading,
    form,
    customersOptions,
  }
}

export default useExpenditureModal
